import React from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { PhoneCall } from "lucide-react";
import { Link } from 'react-router-dom';

const faqData = [
  {
    category: "Product Information",
    items: [
      {
        question: "What is FuelOx Marine?",
        answer: "FuelOx Marine is a premium fuel treatment solution specifically designed for marine engines. As an authorized reseller, we offer this advanced formula that improves fuel combustion efficiency, reduces carbon deposits, and protects fuel systems from corrosion. The product contains advanced detergents, lubricants, and stabilizers that help maintain optimal engine performance in marine environments."
      },
      {
        question: "How does FuelOx Marine work?",
        answer: "FuelOx Marine uses advanced chemistry to clean and protect your engine's fuel system. It removes existing deposits, prevents new ones from forming, helps break down water in your fuel tank, and improves overall combustion efficiency. The formula also contains corrosion inhibitors specifically designed for marine environments."
      },
      {
        question: "How much FuelOx Marine should I use?",
        answer: "The standard dosage recommended by FuelOx is 1 oz per 10 gallons of fuel. For heavily fouled systems or first-time treatments, you may use a double dose (2 oz per 10 gallons). Each bottle comes with a convenient measuring cap to help you add the correct amount."
      },
      {
        question: "Is FuelOx Marine safe for all marine engines?",
        answer: "Yes, FuelOx Marine is safe for use in all gasoline and diesel marine engines. It's compatible with all fuel types and won't void your engine's warranty. The formula meets all environmental regulations for use in waterways and marine sanctuaries."
      }
    ]
  },
  {
    category: "Benefits & Usage",
    items: [
      {
        question: "What benefits can I expect from using FuelOx Marine?",
        answer: "FuelOx Marine provides multiple benefits including: improved fuel economy (up to 15% better efficiency), reduced emissions, smoother engine operation, easier cold starts, extended engine life, protection against ethanol-related issues, and prevention of water-related problems. Many users report noticeable performance improvements after just a few applications."
      },
      {
        question: "How often should I use FuelOx Marine?",
        answer: "For optimal results, FuelOx recommends adding their marine formula to your fuel tank with each fill-up. For seasonal use or boats that sit idle for extended periods, add FuelOx Marine before storage to prevent fuel degradation and protect the fuel system during downtime."
      },
      {
        question: "Does FuelOx Marine help with ethanol-related issues?",
        answer: "Yes, FuelOx Marine is specifically formulated to address common ethanol-related issues in marine engines. It helps prevent phase separation, protects against corrosion, and stabilizes fuel during storage. This is especially important since most retail gasoline contains ethanol (E10)."
      },
      {
        question: "Is FuelOx Marine environmentally friendly?",
        answer: "Yes, FuelOx Marine is formulated to be environmentally responsible. By improving combustion efficiency, it reduces harmful emissions and carbon footprint. The product does not contain any known marine pollutants and meets all environmental regulations for use in waterways."
      }
    ]
  },
  {
    category: "Shipping & Orders",
    items: [
      {
        question: "What are your shipping rates?",
        answer: "As an authorized reseller of FuelOx Marine products, we offer flat rate shipping of $10 for all orders under $100. Orders over $100 qualify for FREE shipping. Currently, we only ship within the continental United States."
      },
      {
        question: "How long does shipping take?",
        answer: "Orders are processed within 24-48 hours of receipt. Standard delivery time is 5-7 business days from the date of shipment. Delivery times may vary based on your location within the continental United States."
      },
      {
        question: "How can I track my order?",
        answer: "Once your order ships, you'll receive a tracking number via email. You can use this tracking number to monitor your delivery status through our shipping partner's website."
      }
    ]
  },
  {
    category: "Returns & Support",
    items: [
      {
        question: "What is your return policy?",
        answer: "Due to the nature of fuel additives, we cannot accept returns once the product has been opened. If you receive a damaged or incorrect item, please contact us within 48 hours of delivery with photos of the damage."
      },
      {
        question: "How can I contact customer support?",
        answer: "As your authorized FuelOx Marine reseller, our customer support team is here to help. You can reach us through email at support@marinefuelboost.com or by phone at (970) 438-2963. We're ready to assist with any questions about FuelOx products or your order."
      }
    ]
  }
];

export default function FAQPage() {
  return (
    <div className="container mx-auto py-12 px-4 mt-16">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4">Frequently Asked Questions</h1>
          <p className="text-lg text-muted-foreground mb-8">
            Find answers to common questions about FuelOx Marine products, shipping, and support. As an authorized reseller, we're here to help you get the most out of your marine engine.
          </p>
          <Link to="/contact">
            <Button variant="outline" className="gap-2">
              <PhoneCall className="w-4 h-4" />
              Need more help? Contact our support team
            </Button>
          </Link>
        </div>
        
        {faqData.map((category, index) => (
          <div key={index} className="mb-12">
            <h2 className="text-2xl font-semibold mb-6">{category.category}</h2>
            <Accordion type="single" collapsible className="w-full">
              {category.items.map((item, itemIndex) => (
                <AccordionItem key={itemIndex} value={`item-${index}-${itemIndex}`}>
                  <AccordionTrigger className="text-left">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent>
                    <p className="text-muted-foreground">{item.answer}</p>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        ))}
      </div>
    </div>
  );
} 